/**
 * Scheduler Export Page
 * 
 * Allows users to export schedulers with custom format, columns, and status filter.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button } from '../components/ui';
import { useAccountStore } from '../store/accountStore';
import { schedulerService } from '../services/scheduler.service';
import type { ExportFormat, SchedulerExportColumn } from '@budgira/shared';
import { SCHEDULER_EXPORT_COLUMNS } from '@budgira/shared';

interface ColumnItem {
  key: SchedulerExportColumn;
  labelKey: string;
  selected: boolean;
}

const SchedulerExportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();
  const { selectedAccount, selectAccount, accounts, fetchAccounts } = useAccountStore();

  // Form state
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [delimiter, setDelimiter] = useState<',' | ';'>(';'); // Default to semicolon for French Excel
  const [status, setStatus] = useState<'all' | 'active' | 'terminated'>('all');
  const [columns, setColumns] = useState<ColumnItem[]>(() => 
    SCHEDULER_EXPORT_COLUMNS.map((col) => ({
      ...col,
      selected: true,
    }))
  );
  const [isExporting, setIsExporting] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Load account if needed
  useEffect(() => {
    if (accounts.length === 0) {
      fetchAccounts();
    }
  }, [accounts.length, fetchAccounts]);

  useEffect(() => {
    if (accountId && accounts.length > 0) {
      const account = accounts.find(a => a.id === accountId);
      if (account && (!selectedAccount || selectedAccount.id !== accountId)) {
        selectAccount(accountId);
      }
    }
  }, [accountId, accounts, selectedAccount, selectAccount]);

  // Toggle column selection
  const toggleColumn = useCallback((key: SchedulerExportColumn) => {
    setColumns(prev => prev.map(col => 
      col.key === key ? { ...col, selected: !col.selected } : col
    ));
  }, []);

  // Select all columns
  const selectAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, selected: true })));
  }, []);

  // Deselect all columns
  const deselectAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, selected: false })));
  }, []);

  // Drag and drop handlers for column reordering
  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setColumns(prev => {
      const newColumns = [...prev];
      const draggedItem = newColumns[draggedIndex];
      if (!draggedItem) return prev;
      newColumns.splice(draggedIndex, 1);
      newColumns.splice(index, 0, draggedItem);
      return newColumns;
    });
    setDraggedIndex(index);
  }, [draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  // Move column up
  const moveColumnUp = useCallback((index: number) => {
    if (index === 0) return;
    setColumns(prev => {
      const newColumns = [...prev];
      const temp = newColumns[index - 1];
      const current = newColumns[index];
      if (temp && current) {
        newColumns[index - 1] = current;
        newColumns[index] = temp;
      }
      return newColumns;
    });
  }, []);

  // Move column down
  const moveColumnDown = useCallback((index: number) => {
    setColumns(prev => {
      if (index === prev.length - 1) return prev;
      const newColumns = [...prev];
      const temp = newColumns[index + 1];
      const current = newColumns[index];
      if (temp && current) {
        newColumns[index] = temp;
        newColumns[index + 1] = current;
      }
      return newColumns;
    });
  }, []);

  // Handle export
  const handleExport = useCallback(async () => {
    if (!accountId) return;

    const selectedColumns = columns
      .filter(col => col.selected)
      .map(col => col.key);

    if (selectedColumns.length === 0) {
      alert(t('schedulerExport.errors.noColumns'));
      return;
    }

    setIsExporting(true);

    try {
      // Build column headers with translations
      const columnHeaders: Record<string, string> = {};
      columns.filter(col => col.selected).forEach(col => {
        columnHeaders[col.key] = t(col.labelKey);
      });

      // Build periodicity labels
      const periodicityLabels: Record<string, string> = {
        'daily': t('schedulers.periodicity.daily'),
        'weekly': t('schedulers.periodicity.weekly'),
        'bi-weekly': t('schedulers.periodicity.biWeekly'),
        'monthly': t('schedulers.periodicity.monthly'),
        'quarterly': t('schedulers.periodicity.quarterly'),
        'semi-annually': t('schedulers.periodicity.semiAnnually'),
        'annually': t('schedulers.periodicity.annually'),
      };

      // Build status labels
      const statusLabels: Record<string, string> = {
        active: t('schedulers.status.active'),
        terminated: t('schedulers.status.terminated'),
      };

      const blob = await schedulerService.export(
        accountId,
        format,
        status,
        selectedColumns,
        format === 'csv' ? delimiter : undefined,
        columnHeaders,
        t('common.yes'),
        t('common.no'),
        periodicityLabels,
        statusLabels
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date and time
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeParts = now.toTimeString().split(' ');
      const timeStr = timeParts[0]?.replace(/:/g, '.') ?? '00.00.00';
      link.download = `schedulers_${dateStr}_${timeStr}.${format}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert(t('schedulerExport.errors.failed'));
    } finally {
      setIsExporting(false);
    }
  }, [accountId, format, delimiter, status, columns, t]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/accounts/${accountId}/schedulers`)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('schedulerExport.backToSchedulers')}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('schedulerExport.title')}
          </h1>
        </div>

        {/* Account Banner */}
        {selectedAccount && (
          <div className="mb-6 bg-gradient-to-r from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 rounded-xl p-4 sm:p-5 shadow-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-white/80 text-xs sm:text-sm">{t('schedulerExport.accountLabel')}</p>
                <p className="text-white font-bold text-lg sm:text-xl">{selectedAccount.name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Format Selection */}
        <Card className="mb-6">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('schedulerExport.format.title')}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
              {(['csv', 'json'] as ExportFormat[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`flex flex-col sm:flex-row items-center justify-center sm:justify-start px-3 sm:px-6 py-3 sm:py-4 rounded-xl border-2 transition-all ${
                    format === f
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="sm:mr-3 mb-1 sm:mb-0">
                    {f === 'csv' ? (
                      <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    )}
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="font-semibold text-sm sm:text-lg">{t(`export.format.${f}`)}</div>
                    <div className="text-xs sm:text-sm opacity-75 hidden sm:block">{t(`export.format.${f}Description`)}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* CSV Delimiter option - only visible when CSV is selected */}
            {format === 'csv' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('export.format.delimiter')}
                </label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="delimiter"
                      value=";"
                      checked={delimiter === ';'}
                      onChange={() => setDelimiter(';')}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                      {t('export.format.delimiterSemicolon')}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="delimiter"
                      value=","
                      checked={delimiter === ','}
                      onChange={() => setDelimiter(',')}
                      className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                      {t('export.format.delimiterComma')}
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Status Filter */}
        <Card className="mb-6">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('schedulerExport.status.title')}
            </h2>
            <div className="flex flex-wrap gap-3">
              {(['all', 'active', 'terminated'] as const).map((s) => (
                <label key={s} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value={s}
                    checked={status === s}
                    onChange={() => setStatus(s)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">
                    {t(`schedulerExport.status.${s}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </Card>

        {/* Column Selection */}
        <Card className="mb-6">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('schedulerExport.columns.title')}
              </h2>
              <div className="flex gap-2 text-sm">
                <button
                  onClick={selectAllColumns}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {t('export.columns.selectAll')}
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={deselectAllColumns}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {t('export.columns.deselectAll')}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('export.columns.hint')}
            </p>
            <div className="space-y-2">
              {columns.map((col, index) => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center p-2 sm:p-3 rounded-lg border transition-all cursor-move ${
                    draggedIndex === index
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 shadow-lg'
                      : col.selected
                      ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                  }`}
                >
                  {/* Drag handle */}
                  <div className="mr-2 sm:mr-3 text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
                    </svg>
                  </div>

                  {/* Checkbox */}
                  <label className="flex items-center flex-1 min-w-0 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={col.selected}
                      onChange={() => toggleColumn(col.key)}
                      className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:bg-gray-700 flex-shrink-0"
                    />
                    <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-900 dark:text-white truncate">
                      {t(col.labelKey)}
                    </span>
                  </label>

                  {/* Move buttons */}
                  <div className="flex gap-0.5 sm:gap-1 flex-shrink-0">
                    <button
                      onClick={() => moveColumnUp(index)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('export.columns.moveUp')}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveColumnDown(index)}
                      disabled={index === columns.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('export.columns.moveDown')}
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Export Button */}
        <div className="flex justify-end gap-4">
          <Button
            variant="secondary"
            onClick={() => navigate(`/accounts/${accountId}/schedulers`)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting || columns.filter(c => c.selected).length === 0}
            className="min-w-[150px]"
          >
            {isExporting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('schedulerExport.exporting')}
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('schedulerExport.exportButton')}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SchedulerExportPage;
